/**
 * DEVLOGS-TEMP — console interception.
 *
 * This is most of "log EVERYTHING" for free: every existing console call across
 * five surfaces becomes a queryable row without touching a single call site.
 *
 * Rules that are not negotiable:
 *  - THE ORIGINAL IS CALLED FIRST, always. A developer's console output must
 *    happen even if the emit path is broken.
 *  - Idempotent. A second install is a no-op (patches carry a tag).
 *  - Uninstall restores the original ONLY if we are still the top patch;
 *    otherwise it leaves the stack alone rather than clobbering someone else's
 *    wrapper (Sentry, React DevTools, a test harness).
 */

import { LEVELS } from "../level.ts";
import type { EmitFn, Level } from "../types.ts";

export interface ConsoleLike {
  log?: (...args: unknown[]) => void;
  info?: (...args: unknown[]) => void;
  warn?: (...args: unknown[]) => void;
  error?: (...args: unknown[]) => void;
  debug?: (...args: unknown[]) => void;
}

const PATCH_TAG = Symbol.for("omnimedia.devlog.console");

const METHOD_LEVELS: ReadonlyArray<readonly [keyof ConsoleLike, Level]> = [
  ["error", LEVELS.error],
  ["warn", LEVELS.warn],
  ["info", LEVELS.info],
  ["log", LEVELS.info],
  ["debug", LEVELS.debug],
];

type Patched = ((...args: unknown[]) => void) & {
  [PATCH_TAG]?: { original: (...args: unknown[]) => void };
};

export function installConsoleCapture(
  target: ConsoleLike,
  emit: EmitFn,
): () => void {
  const installed: Array<{ name: keyof ConsoleLike; patched: Patched; original: Patched }> = [];

  for (const [name, level] of METHOD_LEVELS) {
    const current = target[name] as Patched | undefined;
    if (typeof current !== "function") continue;
    if (current[PATCH_TAG]) continue; // already ours

    const original = current;
    const patched: Patched = function (this: unknown, ...args: unknown[]): void {
      // Original first, unconditionally.
      try {
        original.apply(this, args);
      } catch {
        /* a broken console is not ours to fix */
      }
      try {
        emit(level, "console", String(name), { args }, { msg: firstString(args) });
      } catch {
        /* fire-and-forget */
      }
    };
    patched[PATCH_TAG] = { original };
    target[name] = patched as ConsoleLike[typeof name];
    installed.push({ name, patched, original });
  }

  return () => {
    for (const entry of installed) {
      // Only restore if we are still the top patch.
      if (target[entry.name] === (entry.patched as ConsoleLike[typeof entry.name])) {
        target[entry.name] = entry.original as ConsoleLike[typeof entry.name];
      }
    }
    installed.length = 0;
  };
}

function firstString(args: readonly unknown[]): string | undefined {
  for (const arg of args) {
    if (typeof arg === "string" && arg.length > 0) return arg;
    if (arg instanceof Error) return arg.message;
  }
  return undefined;
}
